import React from "react";
import { Navigate } from "react-router-dom";
import { useIdentity } from "@/auth/identity";
import { isBlockedForNoRole } from "@/auth/claims";
import { Button } from "@/components/ui/button";
import { clearUserScopeFromStorage } from "@/utils/scopeStorage";

/**
 * Inició sesión pero su token no trae rol: está dado de baja o no habilitado. Se muestra en lugar
 * de la app, así no sale ningún pedido a la API (que lo rechazaría con 401) y no parece un chofer
 * sin viajes.
 */
const NoAccessScreen = ({ onLogout }: { onLogout: () => unknown }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-primary p-4">
    <div className="w-full max-w-md p-6 bg-card rounded-lg shadow-xl text-center flex flex-col gap-3">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Millana</h1>
      <h2 className="text-lg font-semibold text-foreground">Tu usuario no tiene acceso a Millana</h2>
      <p className="text-muted-foreground">
        Iniciaste sesión, pero tu usuario no está habilitado. Contactá a tu supervisor.
      </p>
      <Button variant="outline" className="w-full py-6 text-lg" onClick={onLogout}>
        Cerrar sesión
      </Button>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user, logout, provider } = useIdentity();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Cargando...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isBlockedForNoRole(provider, user, import.meta.env.VITE_AUTH0_AUDIENCE)) {
    return (
      <NoAccessScreen
        onLogout={() => {
          clearUserScopeFromStorage(user?.sub ?? null);
          logout({ logoutParams: { returnTo: window.location.origin } });
        }}
      />
    );
  }

  return <>{children}</>;
};
